import React, { useCallback, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";
import moment from "moment";
import PropTypes from "prop-types";
import { showErrorToast, showSuccessToast } from "~/components/Toast";
import { ADD_TIME_RECORD_SUCCESS } from "~/store/action_types/index.jsx";
import {
  editPersonalizedConfigOptions,
  fetchPersonalizedConfigOptions,
} from "~/store/actions/actions.personalizedConfigOptions.jsx";
import { addTimeRecordForTask } from "~/store/actions/actions.timeRecords.jsx";
import { getRawPersonalizedConfigOption } from "~/store/selectors/selector.configOptions.jsx";
import { getUserDefaultJobTypeId } from "~/store/selectors/selector.configOptions.jsx";
import { getActiveJobTypesArrayOfIdAndNameSelector } from "~/store/selectors/selector.jobTypes.jsx";
import { getCanLoggedUserChangeBillableStatus } from "~/store/selectors/selector.tracking.jsx";
import { getLoggedUser } from "~/store/selectors/selector.users.jsx";
import TimeRecordFormBody from "./TimeRecordFormBody.jsx";

const TimeRecordFormBodyContainer = ({
  projectId,
  source,
  recordDate,
  parentId,
  parentType,
  taskId,
  userId,
  onDismiss,
  dateRange,
  displaySuccessToast,
  ...rest
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [submitInProgress, setSubmitInProgress] = useState(false);
  const jobTypes = useSelector((state) => getActiveJobTypesArrayOfIdAndNameSelector(state));
  const defaultJobTypeId = useSelector(getUserDefaultJobTypeId);

  const canSeeBillable = useSelector((state) => getCanLoggedUserChangeBillableStatus(state, { projectId }));
  const loggedUser = useSelector((state) => getLoggedUser(state));
  const defaultShowDescription = useSelector((state) => {
    return getRawPersonalizedConfigOption(state, `time_record_description_expanded`);
  });
  const keepInStopwatch = useSelector((state) => {
    return !!getRawPersonalizedConfigOption(state, "keep_in_stopwatch");
  });

  useEffect(() => {
    dispatch(fetchPersonalizedConfigOptions("keep_in_stopwatch"));
  }, [dispatch]);

  const handleToggleShowDescription = useCallback(
    (value) => {
      dispatch(
        editPersonalizedConfigOptions({
          time_record_description_expanded: value,
        })
      );
    },
    [dispatch]
  );

  const handleKeepInStopwatchChange = useCallback(
    (value) => {
      dispatch(
        editPersonalizedConfigOptions({
          keep_in_stopwatch: value,
        })
      );
    },
    [dispatch]
  );

  const handleSubmit = useCallback(
    (form, keepInStopwatchValue) => {
      setSubmitInProgress(true);
      let date = recordDate;

      if (typeof recordDate === "number") {
        date = moment.unix(recordDate).utc().format("YYYY-MM-DD");
      }

      const attributes = {
        user_id: userId,
        source,
        record_date: date,
        parent_id: parentId,
        parent_type: parentType,
        ...form,
      };

      if (taskId && attributes) {
        attributes.task_id = taskId;
      }

      return dispatch(addTimeRecordForTask(projectId, attributes)).then((action) => {
        if (action && action.error) {
          showErrorToast(t("Failed to save time record"));
          setSubmitInProgress(false);
          return;
        } else if (
          action.type === ADD_TIME_RECORD_SUCCESS &&
          (!dateRange || dateRange.from > moment(recordDate).unix() || dateRange.to < moment(recordDate).unix())
        ) {
          displaySuccessToast && showSuccessToast(t("Time record added"));
        }
        onDismiss(action, keepInStopwatchValue);
      });
    },
    [
      recordDate,
      userId,
      source,
      parentId,
      parentType,
      taskId,
      dispatch,
      projectId,
      dateRange,
      onDismiss,
      t,
      displaySuccessToast,
    ]
  );

  return (
    <TimeRecordFormBody
      {...rest}
      jobTypes={jobTypes}
      defaultJobTypeId={defaultJobTypeId}
      canSeeBillable={canSeeBillable}
      loggedUser={loggedUser ? loggedUser.toJS() : {}}
      defaultShowDescription={defaultShowDescription}
      onToggleShowDescription={handleToggleShowDescription}
      keepInStopwatch={keepInStopwatch}
      onKeepInStopwatchChange={handleKeepInStopwatchChange}
      onSubmit={handleSubmit}
      isSubmitInProgress={submitInProgress}
      source={source}
    />
  );
};

TimeRecordFormBodyContainer.propTypes = {
  projectId: PropTypes.number.isRequired,
  source: PropTypes.string,
  recordDate: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  parentId: PropTypes.number,
  parentType: PropTypes.string,
  taskId: PropTypes.number,
  userId: PropTypes.number,
  onDismiss: PropTypes.func,
  dateRange: PropTypes.shape({
    from: PropTypes.number,
    to: PropTypes.number,
  }),
  displaySuccessToast: PropTypes.bool,
};

TimeRecordFormBodyContainer.defaultProps = {
  displaySuccessToast: true,
};

TimeRecordFormBodyContainer.displayName = "TimeRecordFormBodyContainer";

export default TimeRecordFormBodyContainer;
